import { useEffect, useMemo, useReducer, useState } from "react"
import { ActionIcon, Button, Flex, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core"
import { DataTable, DataTableSortStatus } from "mantine-datatable"
import { IconEdit, IconPlus, IconSearch, IconTrash, IconX } from "@tabler/icons-react"
import { useDeleteEvent, useEvents } from "../hooks/use-events"
import { MSaveEvent } from "../components/MSaveEvent"
import { PAGE_SIZES, ROLES } from "@/shared"
import { useAuth } from "@features/auth/hooks/useAuth.ts"
import { headerStore } from "@shared/state/header.state.ts"
import { openConfirmModal } from "@mantine/modals"
import { useDynamicHeight } from "@/shared/hooks/responsive.queries"
import dayjs from "dayjs"
import _ from "lodash"

interface SearchEvents {
  nombre: string
  lugar: string
}

interface EventsState {
  search: SearchEvents
  sortStatus: DataTableSortStatus
  page: number
  pageSize: number
}

type EventsAction = { type: "SET_SEARCH"; payload: Partial<SearchEvents> } | { type: "SET_SORT_STATUS"; payload: DataTableSortStatus } | { type: "SET_PAGE"; payload: number } | { type: "SET_PAGE_SIZE"; payload: number }

const initialEventsState: EventsState = {
  search: { nombre: "", lugar: "" },
  sortStatus: { columnAccessor: "fecha", direction: "desc" },
  page: 1,
  pageSize: PAGE_SIZES[1]
}

function eventsReducer(state: EventsState, action: EventsAction): EventsState {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: { ...state.search, ...action.payload }, page: 1 }
    case "SET_SORT_STATUS":
      return { ...state, sortStatus: action.payload }
    case "SET_PAGE":
      return { ...state, page: action.payload }
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.payload, page: 1 }
    default:
      return state
  }
}

export default function EventsPage() {
  const { hasRole } = useAuth()
  const { tableHeight } = useDynamicHeight()
  const [state, dispatch] = useReducer(eventsReducer, initialEventsState)
  const { data: events = [], isFetching, refetch } = useEvents()
  const deleteEventMutation = useDeleteEvent()

  const [openedEvent, setOpenedEvent] = useState(false)
  const [editingEvent, setEditingEvent] = useState<any | null>(null)

  useEffect(() => {
    headerStore.setTitle("Eventos")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const filteredEvents = useMemo(() => {
    let result = [...events]
    if (state.search.nombre) {
      result = result.filter((e) => e.nombre?.toLowerCase().includes(state.search.nombre.toLowerCase()))
    }
    if (state.search.lugar) {
      result = result.filter((e) => e.lugar?.toLowerCase().includes(state.search.lugar.toLowerCase()))
    }
    result = _.orderBy(result, [state.sortStatus.columnAccessor], [state.sortStatus.direction])
    return result
  }, [events, state.search, state.sortStatus])

  const paginatedEvents = useMemo(() => {
    const from = (state.page - 1) * state.pageSize
    const to = from + state.pageSize
    return filteredEvents.slice(from, to)
  }, [filteredEvents, state.page, state.pageSize])

  const handleCreateEvent = () => {
    setEditingEvent(null)
    setOpenedEvent(true)
  }

  const handleEditEvent = (event: any) => {
    setEditingEvent(event)
    setOpenedEvent(true)
  }

  const handleDeleteEvent = (id: string) => {
    openConfirmModal({
      title: "Eliminar Evento",
      centered: true,
      children: <Text size="sm">¿Está seguro que desea eliminar este evento? Esta acción no se puede deshacer.</Text>,
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteEventMutation.mutate(id)
    })
  }

  return (
    <Stack gap="md">
      <Flex justify="flex-end">
        {(hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_CREATE)) && (
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreateEvent}>
            Agregar Evento
          </Button>
        )}
      </Flex>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        fetching={isFetching}
        records={paginatedEvents}
        totalRecords={filteredEvents.length}
        recordsPerPage={state.pageSize}
        page={state.page}
        onPageChange={(p) => dispatch({ type: "SET_PAGE", payload: p })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(s) => dispatch({ type: "SET_PAGE_SIZE", payload: s })}
        recordsPerPageLabel="Cantidad por página"
        sortStatus={state.sortStatus}
        onSortStatusChange={(s) => dispatch({ type: "SET_SORT_STATUS", payload: s })}
        columns={[
          {
            accessor: "nombre",
            title: "Nombre del Evento",
            sortable: true,
            filtering: state.search.nombre !== "",
            filter: (
              <TextInput
                placeholder="Buscar nombre"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.nombre && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { nombre: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.nombre}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { nombre: e.currentTarget.value } })}
              />
            )
          },
          { accessor: "fecha", title: "Fecha", sortable: true, render: (r: any) => (r.fecha ? dayjs(r.fecha).format("DD/MM/YYYY") : "-") },
          {
            accessor: "lugar",
            title: "Lugar",
            sortable: true,
            filtering: state.search.lugar !== "",
            filter: (
              <TextInput
                placeholder="Buscar lugar"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.lugar && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { lugar: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.lugar}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { lugar: e.currentTarget.value } })}
              />
            )
          },
          { accessor: "descripcion", title: "Descripción" },
          {
            accessor: "actions",
            title: "Acciones",
            textAlign: "right",
            hidden: !hasRole(ROLES.ADMIN) && !hasRole(ROLES.EDUCATION_EDIT) && !hasRole(ROLES.EDUCATION_DELETE),
            render: (event: any) => (
              <Group gap={4} justify="right" wrap="nowrap">
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_EDIT)) && (
                  <Tooltip label="Editar Evento">
                    <ActionIcon size="sm" variant="subtle" color="blue.7" onClick={() => handleEditEvent(event)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_DELETE)) && (
                  <Tooltip label="Eliminar Evento">
                    <ActionIcon size="sm" variant="subtle" color="red.7" onClick={() => handleDeleteEvent(event.id)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )
          }
        ]}
        style={{ height: tableHeight }}
      />

      <MSaveEvent opened={openedEvent} onClose={() => setOpenedEvent(false)} editingEvent={editingEvent} />
    </Stack>
  )
}
